import React, { useState, useEffect, useRef } from 'react';
import {
  MdDescription, MdFileDownload, MdPictureAsPdf, MdCheckCircle, MdCancel, MdVisibility,
  MdCompareArrows, MdClose,
} from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import { useChangeStream } from '../lib/useChangeStream';
import { useAuth } from '../context/AuthContext';
import { downloadFromApi } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';
import SearchableSelect from '../components/SearchableSelect';

const AuditReport = () => {
  const { hasPermission } = useAuth();
  const canExportExcel = hasPermission('export-excel');
  const canExportPdf   = hasPermission('export-pdf');
  const routeLocation = useLocation();
  const [audits, setAudits] = useState([]);
  // Honour ?audit_id=N from the AssetAudit page's "Report" link so the operator
  // lands directly on the right audit instead of an empty picker.
  const [selectedAuditId, setSelectedAuditId] = useState(() => {
    const q = new URLSearchParams(routeLocation.search);
    return q.get('audit_id') || '';
  });
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [compareWith, setCompareWith] = useState('');
  const [compareData, setCompareData] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    api.get('/audit').then(r => setAudits(r.data)).catch(() => {});
  }, []);

  const loadReport = async (id) => {
    if (!id) { setReport(null); return; }
    setLoading(true);
    try {
      // The new /api/audit/:id/detail endpoint ships per-location stats and
      // the full asset trail per item — much richer than the old
      // /reports/:id payload that only returned {audit, items}.
      const res = await api.get(`/audit/${id}/detail`);
      setReport(res.data);
    } catch {
      showToast('Failed to load report', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport(selectedAuditId);
  }, [selectedAuditId]);

  // The report renders below the (often long) audit-history table, so clicking
  // "View" looked like nothing happened — the report loaded off-screen. When a
  // row is opened via the table we set this flag, then scroll to the report
  // once it has loaded. The change-stream auto-refresh does NOT set the flag,
  // so live updates won't yank the page around.
  const reportRef = useRef(null);
  const scrollPendingRef = useRef(false);
  const viewReport = (id) => {
    scrollPendingRef.current = true;
    setSelectedAuditId(String(id));
  };
  useEffect(() => {
    if (report && scrollPendingRef.current) {
      scrollPendingRef.current = false;
      // small delay so the report DOM is painted before we scroll to it
      setTimeout(() => reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  }, [report]);

  // If the URL changes (back/forward navigation back to a deep-link), pick up
  // the new audit id without a full reload. Including selectedAuditId in deps
  // is safe because the `id !== selectedAuditId` guard prevents the effect
  // from re-firing in a loop after it sets state.
  useEffect(() => {
    const q = new URLSearchParams(routeLocation.search);
    const id = q.get('audit_id') || '';
    if (id && id !== selectedAuditId) setSelectedAuditId(id);
  }, [routeLocation.search, selectedAuditId]);

  // Realtime: refresh audit list and current report on any audit/scan event.
  useChangeStream(() => {
    api.get('/audit').then(r => setAudits(r.data)).catch(() => {});
    if (selectedAuditId) loadReport(selectedAuditId);
  }, ['audit', 'audit_items', 'audit_locations', 'assets']);

  const runCompare = async () => {
    if (!selectedAuditId || !compareWith) return;
    try {
      const r = await api.get('/audit/compare', {
        params: { audit_a: selectedAuditId, audit_b: compareWith },
      });
      setCompareData(r.data);
      setCompareOpen(true);
    } catch (err) {
      showToast(err.response?.data?.error || 'Compare failed', 'error');
    }
  };

  const handleExport = async (type) => {
    if (!selectedAuditId) return;
    try {
      // /api/audit/:id/export/{excel|pdf} produces a multi-sheet Excel
      // (one sheet per location) and a location-sectioned PDF.
      const ext = type === 'excel' ? 'xlsx' : 'pdf';
      // Filename = audit name + download timestamp (falls back to the id).
      const auditName = audits.find(a => String(a.id) === String(selectedAuditId))?.audit_name
        || `audit-${selectedAuditId}`;
      await downloadFromApi(
        `/audit/${selectedAuditId}/export/${type}`,
        tsFilename(auditName, ext),
      );
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  };

  const filteredItems = report?.items?.filter(item => {
    if (filter === 'scanned') return item.scanned;
    if (filter === 'missing') return !item.scanned;
    return true;
  }) || [];

  return (
    <div className="page-container">
      {toast && (
        <div className={`toast toast-${toast.type}`} style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
          {toast.msg}
        </div>
      )}

      <div className="page-header">
        <div>
          <div className="page-title"><MdDescription /> Audit Reports</div>
          <div className="page-subtitle">View and export completed audit reports</div>
        </div>
      </div>

      {/* Historical Audits Table */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div className="card-title"><MdDescription style={{ color: 'var(--accent)' }} /> All Audit History</div>
          <span className="badge badge-info">{audits.length} audits</span>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Audit Name</th>
                <th>Location</th>
                <th>Date & Time</th>
                <th>Audited By</th>
                <th style={{ textAlign: 'center' }}>Total</th>
                <th style={{ textAlign: 'center' }}>Scanned</th>
                <th style={{ textAlign: 'center' }}>Missing</th>
                <th style={{ textAlign: 'center' }}>Completion</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {audits.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                    No audit records found. Run an audit from the Asset Audit or Handheld Simulator page.
                  </td>
                </tr>
              ) : audits.map((a, i) => {
                const pct = a.total_assets > 0 ? Math.round((a.scanned_assets / a.total_assets) * 100) : 0;
                const isSelected = String(a.id) === String(selectedAuditId);
                return (
                  <tr key={a.id}
                    onClick={() => viewReport(a.id)}
                    style={{
                      cursor: 'pointer',
                      background: isSelected ? 'rgba(33,150,243,0.06)' : undefined,
                      outline: isSelected ? '2px solid var(--accent)' : undefined,
                    }}>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{a.audit_name}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        📍 {a.location_name}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {new Date(a.audit_date).toLocaleString()}
                    </td>
                    <td style={{ fontSize: 12 }}>{a.created_by_name || '—'}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{a.total_assets}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--success)' }}>{a.scanned_assets}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: a.missing_assets > 0 ? 'var(--danger)' : 'var(--success)' }}>
                      {a.missing_assets}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, height: 6, background: '#e0e6ef', borderRadius: 3, minWidth: 60 }}>
                          <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`,
                            background: pct === 100 ? 'var(--success)' : pct >= 70 ? 'var(--warning)' : 'var(--danger)',
                            transition: 'width 0.4s' }} />
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 600, minWidth: 32,
                          color: pct === 100 ? 'var(--success)' : pct >= 70 ? 'var(--warning)' : 'var(--danger)' }}>
                          {pct}%
                        </span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`badge ${a.status === 'completed' ? 'badge-success' : 'badge-warning'}`}>
                        {a.status === 'completed' ? '✓ Completed' : '⟳ In Progress'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button className="btn btn-sm btn-outline" title="View Report"
                          onClick={e => { e.stopPropagation(); viewReport(a.id); }}>
                          <MdVisibility />
                        </button>
                        {canExportExcel && (
                          <button className="btn btn-sm btn-success" title="Export Excel"
                            onClick={e => { e.stopPropagation(); setSelectedAuditId(String(a.id)); setTimeout(() => handleExport('excel'), 100); }}>
                            <MdFileDownload />
                          </button>
                        )}
                        {canExportPdf && (
                          <button className="btn btn-sm btn-danger" title="Export PDF"
                            onClick={e => { e.stopPropagation(); setSelectedAuditId(String(a.id)); setTimeout(() => handleExport('pdf'), 100); }}>
                            <MdPictureAsPdf />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected audit export bar */}
      {selectedAuditId && (
        <div ref={reportRef} className="card" style={{ marginBottom: 16, padding: '10px 16px', scrollMarginTop: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>
              Viewing: Audit #{selectedAuditId}
            </span>
            {canExportExcel && (
              <button className="btn btn-success btn-sm" onClick={() => handleExport('excel')}>
                <MdFileDownload /> Export Excel
              </button>
            )}
            {canExportPdf && (
              <button className="btn btn-danger btn-sm" onClick={() => handleExport('pdf')}>
                <MdPictureAsPdf /> Export PDF
              </button>
            )}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <SearchableSelect
                className="form-control form-control-sm"
                value={compareWith}
                onChange={e => setCompareWith(e.target.value)}
                style={{ height: 32, fontSize: 13 }}
              >
                <option value="">Compare with…</option>
                {audits.filter(a => String(a.id) !== String(selectedAuditId)).map(a => (
                  <option key={a.id} value={a.id}>
                    #{a.id} · {a.audit_name?.slice(0, 40)}
                  </option>
                ))}
              </SearchableSelect>
              <button className="btn btn-accent btn-sm" disabled={!compareWith} onClick={runCompare}>
                <MdCompareArrows /> Compare
              </button>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setSelectedAuditId('')} style={{ marginLeft: 'auto' }}>
              ✕ Close Report
            </button>
          </div>
        </div>
      )}

      {/* Compare modal */}
      {compareOpen && compareData && (
        <div className="modal-overlay" onClick={() => setCompareOpen(false)}>
          <div className="modal" style={{ maxWidth: 800 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdCompareArrows /> Audit Comparison</div>
              <button className="btn btn-icon" onClick={() => setCompareOpen(false)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                {[compareData.a, compareData.b].map((aud, idx) => (
                  <div key={aud.id} style={{ padding: 12, background: idx === 0 ? '#e3f2fd' : '#fff3e0', borderRadius: 8 }}>
                    <div style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {idx === 0 ? 'Audit A' : 'Audit B'}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginTop: 4 }}>{aud.audit_name}</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      {new Date(aud.audit_date).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
                {[
                  { label: 'Scanned A → B', val: `${compareData.summary.scannedA} → ${compareData.summary.scannedB}`, color: '#2e7d32' },
                  { label: 'Missing A → B', val: `${compareData.summary.missingA} → ${compareData.summary.missingB}`, color: '#c62828' },
                  { label: 'Found again', val: compareData.summary.foundAgainInB, color: '#1565c0' },
                  { label: 'Newly missing', val: compareData.summary.newlyMissingInB, color: '#ef6c00' },
                  { label: 'Only in A', val: compareData.summary.onlyInA, color: '#888' },
                  { label: 'Only in B', val: compareData.summary.onlyInB, color: '#888' },
                ].map(s => (
                  <div key={s.label} style={{ padding: 10, background: '#f8f9fb', borderRadius: 6, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.val}</div>
                    <div style={{ fontSize: 10, color: '#777', marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {compareData.newlyMissingInB.length > 0 && (
                <details open>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#ef6c00', fontSize: 13, marginBottom: 6 }}>
                    🆕 Newly missing in B ({compareData.newlyMissingInB.length})
                  </summary>
                  <div style={{ background: '#fff8e1', padding: 8, borderRadius: 6, fontSize: 12, maxHeight: 160, overflowY: 'auto' }}>
                    {compareData.newlyMissingInB.map((i, idx) => (
                      <div key={idx} style={{ padding: '3px 0' }}>
                        <strong>{i.asset_name || '?'}</strong> · <code>{i.rfid_tag}</code>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {compareData.foundAgainInB.length > 0 && (
                <details>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#2e7d32', fontSize: 13, marginTop: 8, marginBottom: 6 }}>
                    ✓ Found again in B ({compareData.foundAgainInB.length})
                  </summary>
                  <div style={{ background: '#e8f5e9', padding: 8, borderRadius: 6, fontSize: 12, maxHeight: 160, overflowY: 'auto' }}>
                    {compareData.foundAgainInB.map((i, idx) => (
                      <div key={idx} style={{ padding: '3px 0' }}>
                        <strong>{i.asset_name || '?'}</strong> · <code>{i.rfid_tag}</code>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setCompareOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="loading-spinner"><div className="spinner" /></div>}

      {!loading && report && (
        <>
          {/* Report card */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 16, marginBottom: 16,
            }}>
              {[
                { label: 'Audit ID', value: `#${report.audit.id}` },
                {
                  label: report.locations && report.locations.length > 1 ? 'Locations' : 'Location',
                  value: report.locations && report.locations.length
                    ? report.locations.map(l => l.location_name).join(' · ')
                    : (report.audit.location_name || '—'),
                },
                { label: 'Date', value: new Date(report.audit.audit_date).toLocaleString() },
                { label: 'Audited By', value: report.audit.created_by_name || 'N/A' },
              ].map(f => (
                <div key={f.label} style={{ padding: '12px 14px', background: 'var(--bg)', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{f.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{f.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{
                flex: 1, minWidth: 100, textAlign: 'center', padding: '14px',
                background: '#e8edf5', borderRadius: 10,
              }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>{report.audit.total_assets}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Total</div>
              </div>
              <div style={{
                flex: 1, minWidth: 100, textAlign: 'center', padding: '14px',
                background: '#e8f5e9', borderRadius: 10,
              }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--success)' }}>{report.audit.scanned_assets}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Scanned</div>
              </div>
              <div style={{
                flex: 1, minWidth: 100, textAlign: 'center', padding: '14px',
                background: report.audit.missing_assets > 0 ? '#ffebee' : '#e8f5e9', borderRadius: 10,
              }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: report.audit.missing_assets > 0 ? 'var(--danger)' : 'var(--success)' }}>
                  {report.audit.missing_assets}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Missing</div>
              </div>
              <div style={{
                flex: 1, minWidth: 100, textAlign: 'center', padding: '14px',
                background: '#e3f2fd', borderRadius: 10,
              }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent)' }}>
                  {report.audit.total_assets > 0 ? Math.round((report.audit.scanned_assets / report.audit.total_assets) * 100) : 0}%
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Completion</div>
              </div>
            </div>
          </div>

          {/* Per-location breakdown — only meaningful for multi-location audits */}
          {report.perLocation && report.perLocation.length > 1 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div className="card-title">Per-location breakdown</div>
              </div>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th style={{ textAlign: 'center' }}>Total</th>
                      <th style={{ textAlign: 'center', color: 'var(--success)' }}>Scanned</th>
                      <th style={{ textAlign: 'center', color: 'var(--danger)' }}>Missing</th>
                      <th style={{ textAlign: 'center' }}>Completion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.perLocation.map(stat => {
                      const pct = stat.total > 0 ? Math.round((stat.scanned / stat.total) * 100) : 0;
                      return (
                        <tr key={stat.location_id || 'none'}>
                          <td style={{ fontWeight: 600 }}>{stat.location_name}</td>
                          <td style={{ textAlign: 'center' }}>{stat.total}</td>
                          <td style={{ textAlign: 'center', color: 'var(--success)', fontWeight: 600 }}>{stat.scanned}</td>
                          <td style={{ textAlign: 'center', color: stat.missing > 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
                            {stat.missing}
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 700,
                            color: pct === 100 ? 'var(--success)' : pct >= 70 ? 'var(--warning)' : 'var(--danger)' }}>
                            {pct}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Items table */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Asset Details</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {['all', 'scanned', 'missing'].map(f => (
                  <button key={f} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setFilter(f)}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                    {f === 'scanned' && ` (${report.items.filter(i => i.scanned).length})`}
                    {f === 'missing' && ` (${report.items.filter(i => !i.scanned).length})`}
                    {f === 'all' && ` (${report.items.length})`}
                  </button>
                ))}
              </div>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Asset Name</th>
                    <th>Asset Tag</th>
                    <th>Serial Number</th>
                    <th>RFID Tag</th>
                    <th>Category</th>
                    <th>Current location</th>
                    <th>Last moved</th>
                    <th>Scanned at</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No items</td></tr>
                  ) : filteredItems.map((item, i) => (
                    <tr key={item.id} style={{ background: item.scanned ? '#fafff9' : '#fff5f5' }}>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                      <td style={{ fontWeight: 500 }}>{item.asset_name || 'Unknown'}</td>
                      <td><code style={{ fontSize: 11 }}>{item.asset_tag || '—'}</code></td>
                      <td><code style={{ fontSize: 11 }}>{item.serial_number}</code></td>
                      <td><code style={{ fontSize: 11, color: 'var(--accent)' }}>{item.rfid_tag}</code></td>
                      <td><span className="badge badge-info">{item.category || '—'}</span></td>
                      <td style={{ fontSize: 12 }}>{item.current_location_name || '—'}</td>
                      <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {item.last_moved_at
                          ? <>{new Date(item.last_moved_at).toLocaleDateString()}{item.last_moved_from && <span style={{ color: 'var(--text-muted)' }}> · from {item.last_moved_from}</span>}</>
                          : '—'}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {item.scanned_at ? new Date(item.scanned_at).toLocaleString() : '—'}
                      </td>
                      <td>
                        {item.scanned ? (
                          <span className="badge badge-success" style={{ display: 'inline-flex', gap: 4 }}>
                            <MdCheckCircle /> Scanned
                          </span>
                        ) : (
                          <span className="badge badge-danger" style={{ display: 'inline-flex', gap: 4 }}>
                            <MdCancel /> Missing
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && !report && (
        <div className="card">
          <div className="empty-state">
            <MdDescription />
            <h3>No Report Selected</h3>
            <p>Select a completed audit from the dropdown to view its report.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditReport;
